import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useAuthStore } from './store/authStore'

import { AppShell }   from './components/AppShell'
import { AuthGate }   from './components/AuthGate'
import { AdminShell } from './features/admin/AdminShell'

import { LoginPage }        from './pages/Login'
import { SignupPage }        from './pages/Signup'
import { AcceptInvitePage } from './pages/AcceptInvite'
import { OnboardingPage }   from './pages/Onboarding'
import { HomePage }         from './pages/Home'
import { SettingsPage }     from './pages/Settings'
import { ProfilePage }      from './pages/Profile'
import { LandingPage }           from './pages/LandingPage'
import { PrivacyPolicy }         from './pages/PrivacyPolicy'
import { TermsOfService }        from './pages/TermsOfService'
import { KnowledgeBase }         from './pages/KnowledgeBase'
import { HouseholdSettingsPage } from './pages/HouseholdSettings'
import VerifyEmailPage     from './pages/VerifyEmail'
import SecuritySettingsPage from './pages/SecuritySettings'
import Verify2FAPage       from './pages/Verify2FA'
import NotificationsSettings from './pages/NotificationsSettings'
import DataExport          from './pages/DataExport'
import { AnnouncementsPage } from './features/announcements/AnnouncementsPage'

import { CalendarPage } from './features/calendar/CalendarPage'
import { TasksPage }    from './features/tasks/TasksPage'
import { ShoppingPage } from './features/shopping/ShoppingPage'
import { MealsPage }    from './features/meals/MealsPage'
import { BudgetPage }   from './features/budget/BudgetPage'
import { HealthPage }   from './features/health/HealthPage'
import { PantryPage }   from './features/pantry/PantryPage'
import { VaultPage }    from './features/vault/VaultPage'
import { RewardsPage }  from './features/rewards/RewardsPage'
import { NotesPage }    from './features/notes/NotesPage'
import { MembersPage }  from './features/members/MembersPage'
import { BillingPage }  from './features/billing/BillingPage'
import { RecipesPage }    from './features/recipes/RecipesPage'
import { DocumentsPage }  from './features/documents/DocumentsPage'
import { ContactsPage }   from './features/contacts/ContactsPage'
import { VehiclesPage }   from './features/vehicles/VehiclesPage'
import { MedicationsPage } from './features/medications/MedicationsPage'
import { CustodyPage }    from './features/custody/CustodyPage'
import { BirthdaysPage }  from './features/birthdays/BirthdaysPage'
import { WallPage }       from './features/wall/WallPage'
import { GalleryPage }    from './features/gallery/GalleryPage'
import { MessagesPage }   from './features/messages/MessagesPage'
import DisplayPage from './pages/display/DisplayPage'
import { DisplaySettingsPage } from './features/display-settings/DisplaySettingsPage'

import { DashboardPage }        from './features/admin/pages/DashboardPage'
import { UsersPage }            from './features/admin/pages/UsersPage'
import { UserDetailPage }       from './features/admin/pages/UserDetailPage'
import { HouseholdsPage }       from './features/admin/pages/HouseholdsPage'
import { InvitesPage }          from './features/admin/pages/InvitesPage'
import { LinkedHouseholdsPage } from './features/admin/pages/LinkedHouseholdsPage'
import { BillingAdminPage }     from './features/admin/pages/BillingAdminPage'
import { AuditLogPage }         from './features/admin/pages/AuditLogPage'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 1000 * 60 * 5, retry: 1 },
  },
})

// Scroll-to-top on route change
const ScrollReset = () => {
  const { pathname } = useLocation()
  const prev = useRef(pathname)
  useEffect(() => {
    if (prev.current !== pathname) {
      window.scrollTo(0, 0)
      prev.current = pathname
    }
  }, [pathname])
  return null
}

export default function App() {
  const initAuth = useAuthStore((s) => s.initAuth)

  useEffect(() => {
    const unsub = initAuth()
    return unsub
  }, [initAuth])

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ScrollReset />
        <Routes>
          {/* Wall display — no auth, no shell */}
          <Route path="/display" element={<DisplayPage />} />
          <Route path="/d/:code" element={<DisplayPage />} />

          {/* Public marketing + legal */}
          <Route path="/"        element={<LandingPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms"   element={<TermsOfService />} />
          <Route path="/help"    element={<KnowledgeBase />} />

          {/* Auth */}
          <Route path="/login"         element={<LoginPage />} />
          <Route path="/signup"        element={<SignupPage />} />
          <Route path="/invite/:token" element={<AcceptInvitePage />} />
          <Route path="/verify-2fa"    element={<Verify2FAPage />} />

          {/* Protected — AuthGate wraps AppShell so the shell never renders unauthenticated */}
          <Route
            element={
              <AuthGate>
                <AppShell />
              </AuthGate>
            }
          >
            <Route path="/verify-email"      element={<VerifyEmailPage />} />
            <Route path="/onboarding"       element={<OnboardingPage />} />
            <Route path="/home"             element={<HomePage />} />
            <Route path="/calendar"         element={<CalendarPage />} />
            <Route path="/tasks"            element={<TasksPage />} />
            <Route path="/shopping"         element={<ShoppingPage />} />
            <Route path="/meals"            element={<MealsPage />} />
            <Route path="/budget"           element={<BudgetPage />} />
            <Route path="/health"           element={<HealthPage />} />
            <Route path="/pantry"           element={<PantryPage />} />
            <Route path="/vault"            element={<VaultPage />} />
            <Route path="/rewards"          element={<RewardsPage />} />
            <Route path="/notes"            element={<NotesPage />} />
            <Route path="/members"          element={<MembersPage />} />
            <Route path="/settings"                    element={<SettingsPage />} />
            <Route path="/settings/billing"          element={<BillingPage />} />
            <Route path="/settings/household"        element={<HouseholdSettingsPage />} />
            <Route path="/settings/security"         element={<SecuritySettingsPage />} />
            <Route path="/settings/notifications"    element={<NotificationsSettings />} />
            <Route path="/settings/data-export"      element={<DataExport />} />
            <Route path="/settings/display"          element={<DisplaySettingsPage />} />
            <Route path="/announcements"        element={<AnnouncementsPage />} />
            <Route path="/profile"          element={<ProfilePage />} />
            <Route path="/recipes"          element={<RecipesPage />} />
            <Route path="/documents"        element={<DocumentsPage />} />
            <Route path="/contacts"         element={<ContactsPage />} />
            <Route path="/vehicles"         element={<VehiclesPage />} />
            <Route path="/medications"      element={<MedicationsPage />} />
            <Route path="/custody"          element={<CustodyPage />} />
            <Route path="/birthdays"        element={<BirthdaysPage />} />
            <Route path="/wall"             element={<WallPage />} />
            <Route path="/gallery"          element={<GalleryPage />} />
            <Route path="/messages"         element={<MessagesPage />} />
          </Route>

          {/* Admin — auth-required, super-admin guard, separate desktop layout */}
          <Route
            path="/admin"
            element={
              <AuthGate>
                <AdminShell />
              </AuthGate>
            }
          >
            <Route index                    element={<DashboardPage />} />
            <Route path="users"             element={<UsersPage />} />
            <Route path="users/:id"         element={<UserDetailPage />} />
            <Route path="households"        element={<HouseholdsPage />} />
            <Route path="invites"           element={<InvitesPage />} />
            <Route path="linked-households" element={<LinkedHouseholdsPage />} />
            <Route path="billing"           element={<BillingAdminPage />} />
            <Route path="audit-log"         element={<AuditLogPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/calendar" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
